import type { JSX } from "preact";
import { useEffect } from "preact/hooks";

type PillPickerProps = {
  id: string;
  value: string;
  onChange: (value: string) => void;
  values: Record<string, string>;
  small?: boolean;
};

const scrollToCenter = (container: HTMLElement | null, target: HTMLElement) => {
  if (!container) {
    return;
  }
  const containerRect = container.getBoundingClientRect();
  const targetRect = target.getBoundingClientRect();
  const offset = targetRect.left + targetRect.width / 2 - (containerRect.left + containerRect.width / 2);
  container.scrollTo({ left: container.scrollLeft + offset, behavior: "smooth" });
};

const pillClass = (selected: boolean, small?: boolean): string =>
  `rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 ${small ? "px-2 py-1 text-xs" : "px-4 py-2 text-sm"} ${
    selected
      ? "bg-stone-900 text-white dark:bg-white dark:text-stone-900"
      : "bg-stone-100 text-stone-700 hover:bg-stone-200 dark:bg-stone-800 dark:text-stone-300 dark:hover:bg-stone-700"
  }`;

export const PillPicker = ({ id, values, value: selectedValue, onChange, small }: PillPickerProps) => {
  const handleClick = (event: JSX.TargetedMouseEvent<HTMLButtonElement>, value: string) => {
    if (value !== selectedValue) {
      onChange(value);
    }
    scrollToCenter(event.currentTarget.parentElement?.parentElement ?? null, event.currentTarget);
  };

  // Keeps the selected pill in view when the selection changes, including from the url.
  useEffect(() => {
    const selected = document.getElementById(id + selectedValue);
    if (selected) {
      scrollToCenter(selected.parentElement?.parentElement ?? null, selected);
    }
  }, [selectedValue, id]);

  return (
    <div class="w-full overflow-x-auto scrollbar-hide" id={id}>
      <div class="flex gap-3 min-w-max">
        {Object.entries(values).map(([value, name]) => (
          <button
            type="button"
            id={id + value}
            key={value}
            onClick={(event) => handleClick(event, value)}
            class={pillClass(value === selectedValue, small)}
            data-test-click="pill"
            data-test-id={id}
            data-test-value={value}
          >
            {name}
          </button>
        ))}
      </div>
    </div>
  );
};
